import { cache } from '../../dealer-franchisee/cache-impl/cache-registry';
import { clone, compare } from '../../../webmodule-common/other/clone';
import { CurrencyCode } from '../../api/dealer-api-interface-quote';
import { DevelopmentError } from '../../../webmodule-common/other/development-error';
import { EventBoolean, EventNotify, NullPromise } from '../../../webmodule-common/other/ui/events';
import { fireQuickSuccessToast } from '../../../webmodule-common/other/toast-away';
import { getApiFactory } from '../../api/api-injector';
import { getQuoteContainerManager } from '../../dealer-franchisee/quotes/quote-ui-adapter';
import { isSSI, isShipping } from '../../quotes/data/quote-helper-functions';
import {
  InputUpdatePurchaseOrderStateChangeReason,
  PurchaseOrder,
  PurchaseOrderBatch,
  PurchaseOrderItem,
  PurchaseOrderState,
  ResultGetPurchaseOrder
} from '../../api/dealer-api-interface-franchisee';
import { LangPurchaseOrderContainer } from '../../../localization/scripts/purchase-orders/purchase-order-container-lang';
import { QuoteContainerManager } from '../../quotes/data/quote-container';
import { runEventNotify } from '../../../webmodule-common/other/array-helper';

export class PurchaseOrderContainer {
  purchaseOrderId: string;
  purchaseOrder: PurchaseOrder | null;
  items: PurchaseOrderItem[] | null;
  // The PO's delivery batches (MRD, Task 230006): requested snapshot + V6-committed columns. Loaded with the
  // order; carried through here so the detail view can group items and show requested-vs-committed per batch
  // without re-reading the quote. Empty/absent for an unbatched PO.
  batches: PurchaseOrderBatch[] | null;

  constructor(
    purchaseOrderId: string,
    purchaseOrder: PurchaseOrder | null,
    items: PurchaseOrderItem[] | null,
    batches: PurchaseOrderBatch[] | null = null
  ) {
    this.purchaseOrder = purchaseOrder;
    this.items = items;
    this.batches = batches;
    this.purchaseOrderId = purchaseOrderId;
  }
}

export class PurchaseOrderContainerManager {
  backup: PurchaseOrderContainer;
  container: PurchaseOrderContainer;
  api = getApiFactory().purchaseOrder();
  afterSave: EventNotify[] = [];
  forceLocked?: EventBoolean;
  stateChangeReason: InputUpdatePurchaseOrderStateChangeReason | null = null;
  private _quoteOwnerId?: string | undefined;
  private _quoteMananger?: QuoteContainerManager;

  public get quoteManager(): QuoteContainerManager {
    if (!this._quoteMananger) throw new DevelopmentError("quotemanager doesn't exist. not loaded");
    return this._quoteMananger;
  }
  public get quoteOwnerId(): string {
    if (!this._quoteOwnerId) throw new DevelopmentError('cannot call quoteOwnerId on Order Container before loading');
    return this._quoteOwnerId;
  }

  get items(): PurchaseOrderItem[] {
    return this.container.items ?? [];
  }
  get quoteItemContainers() {
    return this.items.map(pi => this.quoteManager.quoteItemContainer(pi.quoteItemId));
  }

  /** The PO's delivery batches (empty for an unbatched PO). */
  get batches(): PurchaseOrderBatch[] {
    return this.container.batches ?? [];
  }

  /** The batch an item was ordered under (by its requested `quoteItemBatchId`), or undefined when ungrouped. */
  batchFor(quoteItemBatchId: string | null | undefined): PurchaseOrderBatch | undefined {
    if (!quoteItemBatchId) return undefined;
    return this.batches.find(b => b.quoteItemBatchId === quoteItemBatchId);
  }

  /**
   * Quote item commonId → the quote-wide presentation ordinal (1..N), for lines the order carries no
   * recorded batch position for (an unbatched order, or one raised before that snapshot existed).
   *
   * This deliberately MIRRORS the report producer's `GenerateReportsV2.GetItemPresentation`, line for
   * line: every item in presentation order, with the supplier charge lines pushed to the end (SSI first,
   * then shipping), then a contiguous 1..N. The purchase-order screen and the purchase-order PDF must
   * print the same number against the same row, and the PDF has no way to count the order's own rows — so
   * the screen counts the quote's, exactly as the PDF does. Counting the order's rows instead is what made
   * a partial order read 1, 2, 3 here and 3, 5, 7 on the PDF for the same four lines.
   *
   * Reading the quote is safe here (and is not the "self-contained purchase order" rule being broken): a
   * quote is readonly once it has been ordered, so this ordinal cannot drift out from under the order, and
   * the screen already renders every row from the quote's items. The PDF still reads nothing but the
   * order's own rows.
   */
  quotePresentationOrdinals(): Map<string, number> {
    const ordinals = new Map<string, number>();
    const quoteItems = this.quoteManager.container.items ?? [];
    const order = this.quoteManager.container.quotePresentation?.itemDisplayOrder ?? [];

    const sorted: string[] = [];
    const ssi: string[] = [];
    const shipping: string[] = [];

    for (const commonId of order) {
      const item = quoteItems.find(x => x.commonId === commonId);
      if (!item) continue;
      if (isShipping(item)) shipping.push(commonId);
      else if (isSSI(item)) ssi.push(commonId);
      else sorted.push(commonId);
    }

    // Anything the presentation never listed still has to be numbered (it should not happen, but the
    // producer allows for it and the two must not diverge over an edge case).
    for (const item of quoteItems) {
      if (!order.includes(item.commonId)) sorted.push(item.commonId);
    }

    let counter = 0;
    for (const commonId of [...sorted, ...ssi, ...shipping]) {
      if (!ordinals.has(commonId)) ordinals.set(commonId, ++counter);
    }
    return ordinals;
  }

  constructor(original: PurchaseOrderContainer) {
    if (original.purchaseOrder && original.purchaseOrder.id !== original.purchaseOrderId)
      throw new Error('Invalid container configuration. PurchaseOrderId must match purchaseOrderId');
    this.container = original;
    this.backup = clone(this.container);
  }

  get purchaseOrderId(): string {
    return this.container.purchaseOrderId;
  }

  protected async doAfterSave(): Promise<void> {
    await runEventNotify(this.afterSave);
  }

  public get purchaseOrder(): PurchaseOrder {
    if (!this.container.purchaseOrder) {
      throw new Error('Purchase order is empty');
    }

    return this.container.purchaseOrder;
  }

  public get purchaseOrderCurrency(): CurrencyCode {
    return this.purchaseOrder.purchaseOrderCurrency;
  }

  public get purchaseOrderTitle(): string {
    return LangPurchaseOrderContainer.purchaseOrderTitle({
      purchaseOrderNumber: this.purchaseOrder.purchaseOrderNumber,
      title: this.purchaseOrder.title
    });
  }
  isLockedFromUse(): boolean {
    return this.forceLocked?.() ?? false;
  }

  public isReadOnly() {
    return this.isLockedFromUse() || !(this.purchaseOrder.state == PurchaseOrderState.Draft);
  }
  async getFreshOrderCopy(): NullPromise<ResultGetPurchaseOrder> {
    return await this.api.getPurchaseOrder({
      purchaseOrderId: this.purchaseOrderId
    });
  }
  public async needsPurchaseOrder(): Promise<boolean> {
    const sortPurchaseOrderItems = async (result: ResultGetPurchaseOrder) => {
      const quoteManager = getQuoteContainerManager(result.purchaseOrder.branchQuoteId);
      await quoteManager.needsQuote();
      await quoteManager.needsQuoteItems();
      this._quoteMananger = quoteManager;
      this._quoteOwnerId = quoteManager.quote.quoteOwnerId;
      const sortedItems: PurchaseOrderItem[] = [];
      quoteManager.container.items?.forEach(item => {
        const orderItem = result.purchaseOrderItems.find(poi => poi.quoteItemId == item.id);
        if (orderItem) {
          sortedItems.push(orderItem);
        }
      });
      return sortedItems;
    };

    if (!this.container.purchaseOrder) {
      const result = await this.api.getPurchaseOrder({
        purchaseOrderId: this.purchaseOrderId
      });

      if (result) {
        const sortedItems = await sortPurchaseOrderItems(result);
        this.resetPurchaseOrder(
          result.purchaseOrder as unknown as PurchaseOrder,
          sortedItems,
          result.purchaseOrderBatches ?? []
        );
        this.fetchCache();
      } else return false;
    }

    return true;
  }

  protected flushCaches() {
    cache().purchaseOrder.flush([this.purchaseOrderId]);
    cache().projectResourceLink.flush([this.purchaseOrderId]);
  }

  async fetchCache() {
    await Promise.allSettled([
      cache().purchaseOrder.preFetch([this.purchaseOrderId]),
      cache().projectResourceLink.preFetch([this.purchaseOrderId])
    ]);
  }

  public async savePurchaseOrder(silentSave = true): Promise<boolean> {
    this.flushCaches();
    const result = await this.api.updatePurchaseOrder({
      purchaseOrder: this.purchaseOrder,
      stateChangeReason: this.stateChangeReason
    });

    if (result) {
      // A PO save (state/header edits) does not touch batches — they are set at PO creation and updated only
      // by the order-status poller — so preserve the batches already loaded.
      this.resetPurchaseOrder(result.purchaseOrder, result.purchaseOrderItems, this.container.batches ?? []);
      this.fetchCache();

      if (!silentSave) fireQuickSuccessToast(LangPurchaseOrderContainer.purchaseOrderSaved());

      await this.doAfterSave();

      return true;
    }

    return false;
  }

  public purchaseOrderChanged(): boolean {
    return !compare(this.backup.purchaseOrder, this.container.purchaseOrder);
  }

  public changed(): boolean {
    return !compare(this.backup, this.container);
  }

  protected resetPurchaseOrder(
    purchaseOrder: PurchaseOrder,
    purchaseOrderItems: PurchaseOrderItem[],
    purchaseOrderBatches: PurchaseOrderBatch[] = []
  ) {
    this.container.purchaseOrder = purchaseOrder;
    this.container.items = purchaseOrderItems;
    this.container.batches = purchaseOrderBatches;

    this.backup.purchaseOrder = clone(purchaseOrder);
    this.backup.items = clone(purchaseOrderItems);
    this.backup.batches = clone(purchaseOrderBatches);
  }
}
